import {Injectable} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {DeviceId, ConsoleSessionId, ProjectId} from '../../types/ids';
import {isNoUUID} from '../../utils/utils';
import {ConsoleSession} from '../../types/console-session';
import {ConsoleSessionApi} from '../../api/rest/consoleSession.restapi';
import {map, mergeMap} from 'rxjs/operators';
import {ConsoleSessionStatistics, ConsoleStatistics} from '../../types/SessionStatistics';
import {withResultCache} from "@webmate-web-core/shared/utils/cache-util";
import {GenericApiResponse} from '../../api/apibase.api';

@Injectable({
    providedIn: 'root'
})
export class ConsoleSessionService {

    //  _____ _   _ _______ ______ _____  _   _          _
    // |_   _| \ | |__   __|  ____|  __ \| \ | |   /\   | |
    //   | | |  \| |  | |  | |__  | |__) |  \| |  /  \  | |
    //   | | | . ` |  | |  |  __| |  _  /| . ` | / /\ \ | |
    //  _| |_| |\  |  | |  | |____| | \ \| |\  |/ ____ \| |____
    // |_____|_| \_|  |_|  |______|_|  \_\_| \_/_/    \_\______|

    private _consoleSessionsById: { [s: string /* ConsoleSessionId*/]: ReplaySubject<ConsoleSession> }; //cache for ConsoleSession, the "main one"
    private _consoleIdByDevice: { [s: string /* DeviceId*/]: ConsoleSessionId }; //convenience-access object

    constructor(private consoleSessionApi: ConsoleSessionApi) {
        this._consoleSessionsById = {};
        this._consoleIdByDevice = {};
    }

    private _isSessionCached(sessionId: ConsoleSessionId): boolean {
        return this._consoleSessionsById[sessionId] !== undefined;
    }

    private _isSessionOnDeviceCached(deviceId: DeviceId): boolean {
        return this._consoleIdByDevice[deviceId] !== undefined;
    }

    /** does what the name says, returns an Observable upon the new Session in the Cache */
    private _storeSessionInCacheIfNecessary(s: ConsoleSession): ConsoleSession {
        if (this._consoleSessionsById[s.id] === undefined) {
            let newSubject$ = new ReplaySubject<ConsoleSession>(1);
            newSubject$.next(s);
            this._consoleSessionsById[s.id] = newSubject$;
        }
        return s;
    }

    //   _____ ______ _______      _______ _____ ______             _____ _____
    //  / ____|  ____|  __ \ \    / /_   _/ ____|  ____|      /\   |  __ \_   _|
    // | (___ | |__  | |__) \ \  / /  | || |    | |__        /  \  | |__) || |
    //  \___ \|  __| |  _  / \ \/ /   | || |    |  __|      / /\ \ |  ___/ | |
    //  ____) | |____| | \ \  \  /   _| || |____| |____    / ____ \| |    _| |_
    // |_____/|______|_|  \_\  \/   |_____\_____|______|  /_/    \_\_|   |_____|

    /** returns ConsoleSession, or more precisely an Observable which will contain the Session once available. */
    getConsoleSessionFromCache$(consoleId: ConsoleSessionId): Observable<ConsoleSession> {
        if (isNoUUID(consoleId)) {
            throw new ReferenceError('\'getConsoleSessionFromCache$\' called with an illegal ID: \'' + consoleId + '\'');
        }
        if (this._isSessionCached(consoleId)) {
            return this._consoleSessionsById[consoleId].asObservable();
        } else {
            throw new Error('Trying to load console from cache which does not exist.');
        }
    }

    /** returns ConsoleSession, or more precisely an Observable which will contain the Session once available. */
    requestConsoleSession$(deviceId: DeviceId): Observable<ConsoleSession> {
        if (isNoUUID(deviceId)) {
            throw new ReferenceError('\'requestConsoleSession$\' called with an illegal ID: \'' + deviceId + '\'');
        }
        if (this._isSessionOnDeviceCached(deviceId)) {
            return this._consoleSessionsById[deviceId].asObservable();
        } else {
            //when we have no ConsoleSession cached on this Device, request a new one from the BackEnd
            return this.consoleSessionApi.requestConsoleOnDevice(deviceId).pipe(map(session => this._storeSessionInCacheIfNecessary(session)));
        }
    }

    getHeadlessConsoleWebRtcSignalingURL(consoleSessionId: ConsoleSessionId): string {
        return this.consoleSessionApi.getHeadlessConsoleWebRtcSignalingURL(consoleSessionId);
    }

    /** Explicitly releases the ConsoleAccess ticket for this session, instead of waiting on the server's keepalive timeout sweep. */
    terminateConsoleSession$(consoleSessionId: ConsoleSessionId): Observable<GenericApiResponse> {
        return this.consoleSessionApi.terminateConsoleOnDevice(consoleSessionId);
    }

    /**
     * Returns all console sessions for current customer within given time span.
     */
    getConsoleSessionsForCurrentCustomer$(count?: number, from?: string, until?: string): Observable<ConsoleSession[]> {
        let sessionsOfCustomer = new ReplaySubject<ConsoleSession[]>(1);
        return this.consoleSessionApi.getConsoleSessionsForCurrentCustomer(count, from, until)
            .pipe(map(arrOfSessions => sessionsOfCustomer.next(arrOfSessions)), mergeMap(afterwards => sessionsOfCustomer));
    }

    /**
     * Returns statistics for console sessions for current customer within given time span, aggregated by platform.
     */
    getConsoleSessionStatisticsForCurrentCustomer$(count?: number, from?: string, until?: string, project?: ProjectId): Observable<ConsoleSessionStatistics[]> {
        let sessionsOfCustomer = new ReplaySubject<ConsoleSessionStatistics[]>(1);
        //TODO api call
        return this.consoleSessionApi.getConsoleSessionStatisticsForCurrentCustomer(count, from, until, project)
            .pipe(map(arrOfSessions => sessionsOfCustomer.next(arrOfSessions)), mergeMap(afterwards => sessionsOfCustomer));
    }

    /**
     * Returns all console sessions for current customer within given time span, aggregated by user.
     */
    getConsoleUserStatisticsForCurrentCustomer$(count?: number, from?: string, until?: string, project?: ProjectId): Observable<ConsoleSessionStatistics[]> {
        let sessionsOfCustomer = new ReplaySubject<ConsoleSessionStatistics[]>(1);
        //TODO api call
        return this.consoleSessionApi.getConsoleUserStatisticsForCurrentCustomer(count, from, until, project)
            .pipe(map(arrOfSessions => sessionsOfCustomer.next(arrOfSessions)), mergeMap(afterwards => sessionsOfCustomer));
    }

    /**
     * Get console session by id.
     */
    getConsoleSession$(consoleSessionId: string, forceRefresh: boolean = false): Observable<ConsoleSession> {
        return withResultCache<ConsoleSession>(() => this.consoleSessionApi.getConsoleSessionById(consoleSessionId), this._consoleSessionsById, consoleSessionId, forceRefresh);
    }

    /**
     * Returns all console sessions for current customer within given time span.
     */
    getConsoleStatisticsForCurrentCustomer$(from?: string, until?: string, project?: ProjectId): Observable<ConsoleStatistics> {

        return this.consoleSessionApi.getConsoleStatisticsForCurrentCustomer(from, until, project);
    }

    /**
     * Returns all console sessions for current customer within given time span for all projects, grouped by project.
     */
    getConsoleStatisticsForCurrentCustomerGlobalPerProject$(from?: string, until?: string): Observable<ConsoleStatistics> {

        return this.consoleSessionApi.getConsoleStatisticsForCurrentCustomerGlobalPerProject(from, until);
    }

}
